import React, { useState, useEffect, useRef } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button, Modal } from 'react-bootstrap';
import './ImageCropper.css';

/**
 * Компонент для кропа изображений
 * @param {string} props.image - URL изображения для кропа
 * @param {boolean} props.show - Флаг отображения модального окна
 * @param {function} props.onHide - Функция, вызываемая при закрытии модального окна
 * @param {function} props.onCropComplete - Функция, вызываемая при завершении кропа
 * @param {number} props.aspectRatio - Соотношение сторон для кропа (ширина/высота)
 */
export const ImageCropper = ({ image, show, onHide, onCropComplete, aspectRatio: initialAspectRatio = 16 / 9 }) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [aspect, setAspect] = useState(initialAspectRatio);
  const imgRef = useRef(null);

  // При открытии модального окна сбрасываем кроп
  useEffect(() => {
    if (show && imgRef.current) {
      const { width, height } = imgRef.current;
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          aspect || 16 / 9,
          width,
          height
        ),
        width,
        height
      );
      setCrop(crop);
    }
  }, [show, aspect]);

  // Функция для применения пропорций
  const applyAspectRatio = (newAspect) => {
    setAspect(newAspect);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      if (newAspect) {
        // Если задана пропорция, применяем ее
        const crop = centerCrop(
          makeAspectCrop(
            {
              unit: '%',
              width: 90,
            },
            newAspect,
            width,
            height
          ),
          width,
          height
        );
        setCrop(crop);
      } else {
        // Если пропорция не задана (свободный режим), создаем квадратный кроп
        setCrop({
          unit: '%',
          x: 25,
          y: 25,
          width: 50,
          height: 50,
        });
      }
    }
  };

  // Функция для сохранения результата
  const handleSave = () => {
    if (completedCrop && imgRef.current) {
      // Создаем canvas для получения кропнутого изображения
      const canvas = document.createElement('canvas');
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        imgRef.current,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY
      );
      
      // Преобразуем canvas в blob и вызываем onCropComplete
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedImageUrl = URL.createObjectURL(blob);
          onCropComplete({
            croppedImageUrl,
            crop: completedCrop,
            blob
          });
          onHide();
        }
      });
    }
  };

  // Обработчик загрузки изображения
  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    
    // Устанавливаем начальный кроп при загрузке изображения
    if (aspect) {
      const { width, height } = e.currentTarget;
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          aspect,
          width,
          height
        ),
        width,
        height
      );
      setCrop(crop);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Редактирование изображения</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="cropper-container">
          {image && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              className="react-crop-container"
            >
              <img 
                src={image} 
                onLoad={onImageLoad} 
                alt="Crop me" 
                style={{ maxWidth: '100%' }} 
              />
            </ReactCrop>
          )}
        </div>
        <div className="cropper-controls mt-3">
          <p className="form-label mb-1">Пропорции:</p>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-secondary" size="sm" onClick={() => applyAspectRatio(16 / 9)}>16:9</Button>
            <Button variant="outline-secondary" size="sm" onClick={() => applyAspectRatio(4 / 3)}>4:3</Button>
            <Button variant="outline-secondary" size="sm" onClick={() => applyAspectRatio(1)}>1:1</Button>
            <Button variant="outline-secondary" size="sm" onClick={() => applyAspectRatio(undefined)}>Свободные</Button>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Отмена
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Сохранить
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
